import type { TrajectoryWidgetStateParams } from '@repo/go-widgets/trajectory';
import { action, makeObservable, observable } from 'mobx';

import { BaseWidgetEntityWithWellId } from 'src/entities/widget/BaseWidgetEntityWithWellId';
import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';
import { requireService } from 'src/packages/di';
import { COLS, ROWS_COUNT } from 'src/pages/dashboard/features/workbench/consts';

export type TTrajectoryPlanOrFact = 'plan' | 'fact';

interface TrajectoryWidgetOptions {
  type: string;
  stateParams: TrajectoryWidgetStateParams;
  wellId: number | null;
  wellboreId: number | null;
  trajectoryId: number | null;
  planOrFact?: TTrajectoryPlanOrFact | null;
  restoredFromSnapshot?: boolean;
}

export type TTrajectorySerializedInternalState = {
  stateParams: TrajectoryWidgetStateParams;
  wellId: number | null;
  wellboreId: number | null;
  trajectoryId: number | null;
  planOrFact: TTrajectoryPlanOrFact;
};

export class TrajectoryWidgetEntity extends BaseWidgetEntityWithWellId {
  /** Min size: 50% width × 60% height of the workbench grid. */
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: {},
    maxInPixels: null,
    min: {
      w: Math.round(COLS * 0.5),
      h: Math.round(ROWS_COUNT * 0.6),
    },
    max: {
      w: COLS,
      h: ROWS_COUNT,
    },
  };

  @observable type: string;
  @observable wellId: number | null;
  @observable wellboreId: number | null;
  @observable trajectoryId: number | null;
  @observable planOrFact: TTrajectoryPlanOrFact;
  @observable.ref stateParams: TrajectoryWidgetStateParams;
  readonly restoredFromTabSnapshot: boolean;
  @observable inActiveTab: boolean = false;
  /** Blocks chart pointer events while any widget is dragged/resized on the workbench. */
  @observable isInteractionBlocked: boolean = false;

  private disposeHostStore: (() => void) | null = null;

  constructor(
    {
      type,
      stateParams,
      wellId,
      wellboreId,
      trajectoryId,
      planOrFact,
      restoredFromSnapshot: restoredFromTabSnapshot,
    }: TrajectoryWidgetOptions,
    widgetOptions: TWidgetOptions,
    private readonly i18 = requireService('i18'),
  ) {
    super(widgetOptions);

    this.type = type;
    this.wellId = wellId;
    this.wellboreId = wellboreId;
    this.trajectoryId = trajectoryId;
    this.planOrFact = planOrFact === 'plan' || planOrFact === 'fact' ? planOrFact : 'fact';
    this.stateParams = stateParams;
    this.restoredFromTabSnapshot = restoredFromTabSnapshot ?? false;

    makeObservable(this);
  }

  getNameT(): string {
    return 'widgets:Trajectory.title';
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setWellboreId(wellboreId: number | null): void {
    this.wellboreId = wellboreId;
  }

  @action.bound
  setTrajectoryId(trajectoryId: number | null): void {
    this.trajectoryId = trajectoryId;
  }

  @action.bound
  setPlanOrFact(planOrFact: TTrajectoryPlanOrFact): void {
    this.planOrFact = planOrFact;
  }

  @action.bound
  setStateParams(stateParams: TrajectoryWidgetStateParams): void {
    this.stateParams = stateParams;
  }

  @action.bound
  setFullscreen(isFullscreen: boolean): void {
    this.isFullscreen = isFullscreen;
  }

  get isControlWidget(): boolean {
    return false;
  }

  get title(): string {
    return this.i18.t(this.getNameT());
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
  }

  @action.bound
  setInteractionBlocked(value: boolean): void {
    this.isInteractionBlocked = value;
  }

  @action.bound
  setDisposeHostStore(dispose: (() => void) | null): void {
    this.disposeHostStore = dispose;
  }

  override destroy() {
    this.disposeHostStore?.();
    this.disposeHostStore = null;
  }
}
